import { BehaviorSubject, fromEvent, merge, Observable, timer } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { Injectable, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export interface ViewportDimensions {
  width: number;
  height: number;
  isIframe: boolean;
  scale: number;
}

@Injectable({
  providedIn: 'root'
})
export class ViewportMonitorService implements OnDestroy {
  private viewportSubject = new BehaviorSubject<ViewportDimensions>(this.getCurrentDimensions());
  public viewport$: Observable<ViewportDimensions> = this.viewportSubject.asObservable();

  private resizeObserver: ResizeObserver | null = null;
  private mutationObserver: MutationObserver | null = null;

  private targetResolution: { width: number; height: number } | null = null;

  constructor() {
    this.initializeMonitoring();
  }

  /**
   * Set the target resolution for accurate preview rendering
   * @param width - Target screen width in pixels
   * @param height - Target screen height in pixels
   */
  public setTargetResolution(width: number | null, height: number | null): void {
    if (width && height && width > 0 && height > 0) {
      this.targetResolution = { width, height };
    } else {
      this.targetResolution = null;
    }
    this.forceRecalculation();
  }

  private initializeMonitoring(): void {
    const viewportChanges$ = merge(
      fromEvent(window, 'resize'),
      fromEvent(window, 'orientationchange'),
      fromEvent(document, 'visibilitychange'),
      fromEvent(window, 'focus'),
      fromEvent(document, 'fullscreenchange'),
      fromEvent(document, 'webkitfullscreenchange'),
      fromEvent(document, 'mozfullscreenchange'),
      timer(0, 5000)
    ).pipe(
      debounceTime(100),
      map(() => this.getCurrentDimensions()),
      distinctUntilChanged((a, b) =>
        a.width === b.width &&
        a.height === b.height &&
        a.isIframe === b.isIframe
      ),
      startWith(this.getCurrentDimensions())
    );

    viewportChanges$.pipe(takeUntilDestroyed()).subscribe(dimensions => {
      this.viewportSubject.next(dimensions);
    });

    // Setup ResizeObserver for more reliable detection
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.viewportSubject.next(this.getCurrentDimensions());
      });

      // Observe both body and documentElement
      this.resizeObserver.observe(document.body);
      this.resizeObserver.observe(document.documentElement);
    }

    // Setup MutationObserver to detect DOM changes that might affect layout
    if (typeof MutationObserver !== 'undefined') {
      this.mutationObserver = new MutationObserver(() => {
        // Check if viewport changed due to DOM mutations
        const current = this.getCurrentDimensions();
        const last = this.viewportSubject.value;
        if (current.width !== last.width || current.height !== last.height) {
          this.viewportSubject.next(current);
        }
      });

      this.mutationObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['style', 'class'],
        subtree: false
      });
    }
  }

  private getCurrentDimensions(): ViewportDimensions {
    const isIframe = window.self !== window.top;

    // Use target resolution if set, otherwise fall back to window dimensions
    let width: number;
    let height: number;
    let scale: number;

    if (this.targetResolution) {
      width = this.targetResolution.width;
      height = this.targetResolution.height;

      scale = 1.0;
    } else {
      width = window.innerWidth;
      height = window.innerHeight;

      // Calculate a scale factor based on standard 1920x1080 resolution
      const baseWidth = 1920;
      const baseHeight = 1080;
      const widthScale = width / baseWidth;
      const heightScale = height / baseHeight;
      scale = Math.min(widthScale, heightScale, 1.5);
    }

    return { width, height, isIframe, scale };
  }

  /**
   * Force a recalculation of viewport dimensions
   */
  public forceRecalculation(): void {
    this.viewportSubject.next(this.getCurrentDimensions());
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }
}